import React, { useEffect } from 'react';
import isFunction from 'lodash/isFunction';
import { useDispatch, useSelector } from 'react-redux';
import {
  expressCheckoutWidgetLoadingFinished,
  expressCheckoutMorePaymentOptionsButtonClicked,
} from '@wix/bi-logger-cashier-ugc/v2';
import { useBiLogger } from '../Hoc/BiLogger/BiLoggerProvider';
import { useFedopsLogger } from '../Hoc/Fedops/FedopsProvider';
import { BiOriginCart, BiOriginProduct } from '../Hoc/BiLogger/constants';
import { InteractionName } from '../../types/InteractionName';
import {
  selectHighestSupportedApplePayVersion,
  selectTopProps,
  selectOnClick,
  selectIsPayPalButtonAvailable,
  selectApplePayButtonAvailable,
  selectGooglePayButtonAvailable,
  selectAvailablePmsList,
  isVerticallyCollapsed,
} from '../../selectors';
import { expandWidget } from '../../actions/expandWidget';
import { PaymentMethodsListView } from './view';

export function PaymentMethodsListController() {
  const {
    layout,
    currency,
    meta,
    orderItems,
    horizontalLayoutDividerClassName,
    horizontalLayoutHeaderClassName,
    onPaymentAuthorizedInit,
    onPaymentAuthorized,
    onShippingContactSelected,
    onPaymentMethodsAmountInit,
  } = useSelector(selectTopProps);

  const onClick = useSelector(selectOnClick);
  const dispatch = useDispatch();
  const biLogger = useBiLogger();
  const fedopsLogger = useFedopsLogger();

  const hasGooglePay = useSelector(selectGooglePayButtonAvailable);
  const hasPayPal = useSelector(selectIsPayPalButtonAvailable);
  const hasApplePay = useSelector(selectApplePayButtonAvailable);
  const applePayVersion = useSelector(selectHighestSupportedApplePayVersion);
  const isCollapsed = useSelector(isVerticallyCollapsed);
  const availablePmsList = useSelector(selectAvailablePmsList);
  const onMorePaymentOptionsClick = React.useCallback(() => {
    biLogger.report(
      expressCheckoutMorePaymentOptionsButtonClicked({
        appInstanceId: meta.appInstanceId,
        origin: meta.productId ? BiOriginProduct : BiOriginCart,
        productId: meta.productId,
        msid: meta.siteId,
      }),
    );
    dispatch(expandWidget());
  }, []);

  useEffect(() => {
    fedopsLogger.interactionEnded(InteractionName.FMP);
    fedopsLogger.appLoaded();
    biLogger.report(
      expressCheckoutWidgetLoadingFinished({
        currency,
        flowType: String(applePayVersion),
        origin: meta.productId ? BiOriginProduct : BiOriginCart,
        paymentmethod: getBiPmList(availablePmsList),
        appInstanceId: meta.appInstanceId,
        productId: meta.productId,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFunction(onPaymentMethodsAmountInit)) {
      const amountOfAvailablePMs = [hasGooglePay, hasApplePay, hasPayPal].reduce((acc, el) => (el ? acc + 1 : acc), 0);
      onPaymentMethodsAmountInit(amountOfAvailablePMs);
    }
  }, [orderItems]);

  return (
    <PaymentMethodsListView
      availablePmsList={availablePmsList}
      isCollapsed={isCollapsed}
      layout={layout}
      horizontalLayoutHeaderClassName={horizontalLayoutHeaderClassName}
      horizontalLayoutDividerClassName={horizontalLayoutDividerClassName}
      onMorePaymentOptionsClick={onMorePaymentOptionsClick}
      onClick={onClick}
      onPaymentAuthorized={onPaymentAuthorized}
      onPaymentAuthorizedInit={onPaymentAuthorizedInit}
      onShippingContactSelected={onShippingContactSelected}
    ></PaymentMethodsListView>
  );
}

const getBiPmList = ({ googlePay, applePay, payPal }: ReturnType<typeof selectAvailablePmsList>) => {
  const pms = [];
  if (googlePay) {
    pms.push('googlePay');
  }
  if (applePay) {
    pms.push('applePay');
  }
  payPal.map((funding) => pms.push(funding));
  return pms.join(',');
};
